import React, { useState, useEffect } from 'react';
import {
  Plug, Check, X, RefreshCw, Settings, ExternalLink, AlertCircle, Zap,
  Facebook, Instagram, Linkedin, Twitter, Youtube, Chrome, MapPin
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface Integration {
  id: string;
  name: string;
  platform: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  gradient: string;
  status: 'connected' | 'disconnected' | 'error';
  description: string;
  features: string[];
  connectedAccount?: string;
  lastSync?: string;
  apiVersion?: string;
}

export default function IntegrationsManagement() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    // Mock data for now - replace with actual API call
    const mockIntegrations: Integration[] = [
      {
        id: 'wordpress',
        name: 'WordPress',
        platform: 'wordpress',
        icon: Chrome,
        color: 'bg-blue-600',
        gradient: 'from-blue-500 to-blue-600',
        status: 'disconnected',
        description: 'Publish and manage WordPress blog posts',
        features: ['Auto-publish posts', 'Schedule content', 'Media library sync', 'SEO optimization'],
        apiVersion: 'v2',
      },
      {
        id: 'bluesky',
        name: 'Bluesky',
        platform: 'bluesky',
        icon: Twitter,
        color: 'bg-sky-500',
        gradient: 'from-sky-400 to-sky-600',
        status: 'disconnected',
        description: 'Post to Bluesky social network',
        features: ['Create posts', 'Schedule posts', 'Thread support', 'Analytics'],
        apiVersion: 'v1',
      },
      {
        id: 'pinterest',
        name: 'Pinterest',
        platform: 'pinterest',
        icon: Chrome,
        color: 'bg-red-600',
        gradient: 'from-red-500 to-red-700',
        status: 'disconnected',
        description: 'Create and schedule Pins',
        features: ['Pin creation', 'Board management', 'Idea Pins', 'Analytics'],
        apiVersion: 'v5',
      },
      {
        id: 'threads',
        name: 'Threads',
        platform: 'threads',
        icon: Instagram,
        color: 'bg-gray-900',
        gradient: 'from-gray-800 to-gray-900',
        status: 'disconnected',
        description: 'Post to Threads by Instagram',
        features: ['Text posts', 'Image posts', 'Thread creation', 'Engagement tracking'],
        apiVersion: 'v1',
      },
      {
        id: 'reddit',
        name: 'Reddit',
        platform: 'reddit',
        icon: Chrome,
        color: 'bg-orange-600',
        gradient: 'from-orange-500 to-red-600',
        status: 'disconnected',
        description: 'Post to Reddit communities',
        features: ['Subreddit posting', 'Schedule posts', 'Karma tracking', 'Comment monitoring'],
        apiVersion: 'v1',
      },
      {
        id: 'youtube',
        name: 'YouTube',
        platform: 'youtube',
        icon: Youtube,
        color: 'bg-red-600',
        gradient: 'from-red-500 to-red-700',
        status: 'disconnected',
        description: 'Upload and manage YouTube videos',
        features: ['Video uploads', 'Thumbnail management', 'Description optimization', 'Analytics'],
        apiVersion: 'v3',
      },
      {
        id: 'tiktok',
        name: 'TikTok',
        platform: 'tiktok',
        icon: Chrome,
        color: 'bg-black',
        gradient: 'from-gray-900 to-black',
        status: 'disconnected',
        description: 'Post videos to TikTok',
        features: ['Video upload', 'Caption management', 'Hashtag optimization', 'Trending sounds'],
        apiVersion: 'v1',
      },
      {
        id: 'facebook',
        name: 'Facebook',
        platform: 'facebook',
        icon: Facebook,
        color: 'bg-blue-600',
        gradient: 'from-blue-500 to-blue-700',
        status: 'connected',
        description: 'Manage Facebook Pages and posts',
        features: ['Page posts', 'Stories', 'Events', 'Page insights', 'Ad management'],
        connectedAccount: 'Agency Marketing Page',
        lastSync: '2025-10-03T18:30:00Z',
        apiVersion: 'v18.0',
      },
      {
        id: 'instagram',
        name: 'Instagram',
        platform: 'instagram',
        icon: Instagram,
        color: 'bg-pink-600',
        gradient: 'from-purple-500 via-pink-500 to-orange-500',
        status: 'connected',
        description: 'Post to Instagram feed and stories',
        features: ['Feed posts', 'Stories', 'Reels', 'IGTV', 'Shopping tags', 'Insights'],
        connectedAccount: '@agencymarketing',
        lastSync: '2025-10-03T18:25:00Z',
        apiVersion: 'Graph API v18.0',
      },
      {
        id: 'linkedin',
        name: 'LinkedIn',
        platform: 'linkedin',
        icon: Linkedin,
        color: 'bg-blue-700',
        gradient: 'from-blue-600 to-blue-800',
        status: 'connected',
        description: 'Share on LinkedIn profiles and pages',
        features: ['Post updates', 'Company pages', 'Document sharing', 'Analytics', 'Employee advocacy'],
        connectedAccount: 'Agency Marketing LLC',
        lastSync: '2025-10-03T17:45:00Z',
        apiVersion: 'v2',
      },
      {
        id: 'twitter',
        name: 'X (Twitter)',
        platform: 'twitter',
        icon: Twitter,
        color: 'bg-black',
        gradient: 'from-gray-800 to-black',
        status: 'error',
        description: 'Post tweets and threads',
        features: ['Tweets', 'Threads', 'Media uploads', 'Polls', 'Analytics', 'Scheduled posts'],
        connectedAccount: '@agencymarketing',
        lastSync: '2025-10-02T12:00:00Z',
        apiVersion: 'v2',
      },
      {
        id: 'google-business',
        name: 'Google Business Profile',
        platform: 'google-business',
        icon: MapPin,
        color: 'bg-green-600',
        gradient: 'from-green-500 to-green-700',
        status: 'disconnected',
        description: 'Manage Google Business Profile posts and reviews',
        features: ['Local posts', 'Product posts', 'Review management', 'Q&A', 'Business insights'],
        apiVersion: 'v1',
      },
    ];

    setIntegrations(mockIntegrations);
  };

  const handleConnect = async (integrationId: string) => {
    console.log('Connecting to:', integrationId);
    // Implement OAuth flow or API key setup
  };

  const handleDisconnect = async (integrationId: string) => {
    console.log('Disconnecting:', integrationId);
    // Implement disconnect logic
  };

  const handleRefresh = async (integrationId: string) => {
    console.log('Refreshing:', integrationId);
    // Implement token refresh
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <span className="flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <Check className="h-3 w-3 mr-1" /> Connected
          </span>
        );
      case 'error':
        return (
          <span className="flex items-center px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <AlertCircle className="h-3 w-3 mr-1" /> Error
          </span>
        );
      case 'disconnected':
        return (
          <span className="flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
            <X className="h-3 w-3 mr-1" /> Not Connected
          </span>
        );
      default:
        return null;
    }
  };

  const filteredIntegrations = integrations.filter(integration => {
    const matchesSearch = integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         integration.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || integration.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: integrations.length,
    connected: integrations.filter(i => i.status === 'connected').length,
    disconnected: integrations.filter(i => i.status === 'disconnected').length,
    errors: integrations.filter(i => i.status === 'error').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="mt-1 text-sm text-gray-500">
            Connect your favorite platforms and automate your workflow
          </p>
        </div>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          <Zap className="h-5 w-5 mr-2" />
          Browse Marketplace
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search integrations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-4 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="all">All Status</option>
            <option value="connected">Connected</option>
            <option value="disconnected">Not Connected</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredIntegrations.map((integration) => {
          const Icon = integration.icon;

          return (
            <div
              key={integration.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden"
            >
              {/* Header */}
              <div className={`h-24 bg-gradient-to-br ${integration.gradient} flex items-center justify-center relative`}>
                <div className="absolute top-3 right-3">
                  {getStatusBadge(integration.status)}
                </div>
                <div className="h-16 w-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                  <Icon className={`h-8 w-8 ${integration.color.replace('bg-', 'text-')}`} />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{integration.name}</h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">{integration.description}</p>

                {/* Connected Account Info */}
                {integration.status === 'connected' && integration.connectedAccount && (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-xs text-green-700 font-medium mb-1">Connected Account</p>
                    <p className="text-sm text-green-900">{integration.connectedAccount}</p>
                    {integration.lastSync && (
                      <p className="text-xs text-green-600 mt-1">
                        Last sync: {new Date(integration.lastSync).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                {/* Error State */}
                {integration.status === 'error' && (
                  <div className="mb-4 p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-xs text-red-700 font-medium mb-1">Connection Error</p>
                    <p className="text-sm text-red-900">Authentication expired. Reconnect to continue.</p>
                  </div>
                )}

                {/* Features */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">Features</p>
                  <div className="flex flex-wrap gap-1">
                    {integration.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                        {feature}
                      </span>
                    ))}
                    {integration.features.length > 3 && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        +{integration.features.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  {integration.status === 'connected' ? (
                    <>
                      <button
                        onClick={() => handleRefresh(integration.id)}
                        className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                      >
                        <RefreshCw className="h-4 w-4 mr-1.5" />
                        Refresh
                      </button>
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        className="flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <button className="flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                        <Settings className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleConnect(integration.id)}
                        className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                      >
                        <Plug className="h-4 w-4 mr-1.5" />
                        Connect
                      </button>
                      <button className="flex items-center justify-center px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">
                        <ExternalLink className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>

                {/* API Version */}
                {integration.apiVersion && (
                  <p className="text-xs text-gray-400 mt-3">API {integration.apiVersion}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredIntegrations.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Plug className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations found</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Try adjusting your search terms' : 'Browse the marketplace to find integrations'}
          </p>
        </div>
      )}
    </div>
  );
}
